import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useNavigate } from 'react-router-dom'

import { KTIcon } from '../../_metronic/helpers'
import { createClient } from '../../app/modules/clients/core/_requests'
import { CreateClientRequest } from '../../app/modules/clients/core/_rest_models'
import { getListMerchantsForSelect } from '../../app/common/core/_requests'
import { useAuth } from '../../app/modules/auth'
import { Merchant } from '../../app/common/core/_models'

const AppSwal = withReactContent(Swal)

interface CreateClientProps {
  show: boolean
  onHide: () => void
  onClientCreated?: () => void
}

const createClientSchema = Yup.object().shape({
  merchantToken: Yup.string().required('Выберите мерчанта'),
  externalId: Yup.string()
    .required('Введите ID пользователя')
    .max(50, 'Максимальная длина 50 символов')
})

export const CreateClient: React.FC<CreateClientProps> = ({ show, onHide, onClientCreated }) => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [loading, setLoading] = useState(false)

  const formik = useFormik<CreateClientRequest>({
    initialValues: {
      merchantToken: '',
      externalId: ''
    },
    validationSchema: createClientSchema,
    onSubmit: (values) => {
      handleCreateClient(values)
    }
  })

  const fetchMerchants = async () => {
    setLoadingMerchants(true)
    try {
      const response = await getListMerchantsForSelect(currentUser)
      
      if (response.data?.success && response.data.merchants) {
        setMerchants(response.data.merchants)
      }
    } catch (error) {
      console.error('Ошибка загрузки мерчантов:', error)
      Swal.fire('Ошибка', 'Не удалось загрузить список мерчантов', 'error')
    } finally {
      setLoadingMerchants(false)
    }
  }

  useEffect(() => {
    if (show && merchants.length === 0) {
      fetchMerchants()
    }
  }, [show])

  const handleCreateClient = async (data: CreateClientRequest) => {
    setLoading(true)
    
    try {
      const response = await createClient(data)
      
      if (response.data?.success && response.data.externalUser) {
        formik.resetForm()
        onHide()
        
        AppSwal.fire({
          title: 'Клиент успешно создан',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          // Редирект на профиль клиента
          navigate(`/client/info/${response.data.externalUser!.token}`)
          onClientCreated?.()
        })
      }
    } catch (error: any) {
      console.error('Ошибка создания клиента:', error)
      
      const responseData = error.response?.data
      
      // Проверяем специальный формат ошибок API
      if (responseData?.errors?.general_error) {
        Swal.fire('Ошибка', responseData.errors.general_error, 'error')
      } else if (error.response?.status === 400) {
        // Показываем ошибки валидации
        const errors = responseData?.errors
        if (errors) {
          const errorMessages = Object.values(errors).join('\n')
          Swal.fire('Ошибка валидации', errorMessages, 'error')
        } else {
          Swal.fire('Ошибка', 'Неверные данные', 'error')
        }
      } else if (error.response?.status === 409) {
        Swal.fire('Ошибка', 'Пользователь с таким ID уже существует', 'error')
      } else {
        Swal.fire('Ошибка', 'Произошла ошибка при создании клиента', 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      formik.resetForm()
      onHide()
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      size="lg"
      centered
    >
      <div className="modal-header">
        <h2 className="modal-title">Создать клиента</h2>
        {!loading && (
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={handleClose}
          >
            <KTIcon className="fs-1" iconName="cross" />
          </div>
        )}
      </div>

      <div className="modal-body py-lg-6 px-lg-6">
        <form onSubmit={formik.handleSubmit} noValidate className="form">
          <div className="row mb-6">
            <label className="col-lg-4 col-form-label required fw-bold fs-6">
              Мерчант
            </label>
            <div className="col-lg-8 fv-row">
              <select
                className="form-select form-select-solid form-select-lg fw-bold"
                disabled={loadingMerchants || loading}
                {...formik.getFieldProps('merchantToken')}
              >
                <option value="">
                  {loadingMerchants ? 'Загрузка...' : 'Выберите мерчанта'}
                </option>
                {merchants.map((merchant) => (
                  <option key={merchant.token} value={merchant.token}>
                    {merchant.title}
                  </option>
                ))}
              </select>
              {formik.touched.merchantToken && formik.errors.merchantToken && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.merchantToken}</div>
                </div>
              )}
            </div>
          </div>

          <div className="row mb-6">
            <label className="col-lg-4 col-form-label required fw-bold fs-6">
              Внешний ID пользователя
            </label>
            <div className="col-lg-8 fv-row">
              <input
                type="text"
                className="form-control form-control-lg form-control-solid"
                placeholder="Введите ID пользователя"
                maxLength={50}
                disabled={loading}
                {...formik.getFieldProps('externalId')}
              />
              {formik.touched.externalId && formik.errors.externalId && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.externalId}</div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={handleClose}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || loadingMerchants}
            >
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              Создать
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
} 